import { getAuth, validatePassword } from 'firebase/auth'
import type { PasswordValidationStatus } from 'firebase/auth'
import { useEffect, useRef, useState } from 'react'

const VALIDATION_DEBOUNCE_MS = 300

interface ValidationState {
  status: PasswordValidationStatus | null
  validatedPassword: string | null
  failedPassword: string | null
  validating: boolean
}

export const usePasswordPolicyValidation = (password: string) => {
  const emptyPasswordStatusRef = useRef<PasswordValidationStatus | null>(null)
  const [state, setState] = useState<ValidationState>({
    status: null,
    validatedPassword: null,
    failedPassword: null,
    validating: true,
  })

  useEffect(() => {
    let active = true

    if (!password && emptyPasswordStatusRef.current) {
      setState({
        status: emptyPasswordStatusRef.current,
        validatedPassword: '',
        failedPassword: null,
        validating: false,
      })

      return
    }

    setState(previous => ({
      ...previous,
      validating: true,
      failedPassword: null,
    }))
    const debounce = password ? VALIDATION_DEBOUNCE_MS : 0

    const timeoutId = window.setTimeout(async () => {
      try {
        const status = await validatePassword(getAuth(), password)

        if (!active) return

        if (!password) {
          emptyPasswordStatusRef.current = status
        }
        setState({
          status,
          validatedPassword: password,
          failedPassword: null,
          validating: false,
        })
      } catch {
        if (!active) return

        setState(previous => ({
          ...previous,
          validatedPassword: null,
          failedPassword: password,
          validating: false,
        }))
      }
    }, debounce)

    return () => {
      active = false
      window.clearTimeout(timeoutId)
    }
  }, [password])

  const statusCurrent = state.validatedPassword === password

  return {
    passwordValidationStatus: state.status,
    passwordPolicyStatusCurrent: statusCurrent,
    passwordPolicyValidating: state.validating,
    passwordPolicyValidationFailed: state.failedPassword === password,
    passwordPolicyValid: statusCurrent && state.status?.isValid === true,
  }
}
